// Packages
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from "react-router-dom";
import * as yup from 'yup';

// Models
import useModels from '../../../../models';

// Actions
import useApi from "../../../../api";

const useRolesEdit = () => {
  const [showModal, setShowModal] = useState(false);
  const [listPermissions, setListPermissions] = useState([]);
  const [groupedPermissions, setGroupedPermissions] = useState([]);
  const navigation = useNavigate();

  // Models
  const { useSelectors } = useModels();
  const { useSelector, useRolesSelectors } = useSelectors();
  const { roleSelected } = useRolesSelectors();

  const roleData = useSelector(roleSelected);

  // Actions
  const { useActions } = useApi();
  const { useRolesActions, useGeneralActions } = useActions();
  const { actEditRole } = useRolesActions();
  const { actListPermissions } = useGeneralActions();

  // Validation schema
  const editRoleValidationSchema = yup.object().shape({
    name: yup.string().required('El nombre es requerido'),
    permissions: yup.array(),
  });

  const {
    control,
    handleSubmit,
    reset,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(editRoleValidationSchema),
    defaultValues: {
      name: roleData?.name || '',
      permissions: roleData?.permissions?.map(p => p.id) || [],
    }
  });

  const handleCloseModal = () => {
    setShowModal(false);
    navigation(-1);
  };

  const handleShowModal = () => {
    setShowModal(true);
  };

  const onSubmit = (data) => {
    // Filter out parent IDs that have children - keep standalone permissions
    const childPermissions = data.permissions?.filter(permId => {
      // Find the group for this permission
      const group = groupedPermissions.find(g => g.parent.value === permId);
      
      // If it's not a parent at all, keep it (it's a child being sent)
      if (!group) return true;
      
      // If it's a parent but has no children (standalone), keep it
      if (group.children.length === 0) return true;
      
      // If it's a parent with children, filter it out
      return false;
    }) || [];

    // Send name and permissions array (only children and standalone)
    actEditRole(roleData.id, {
      name: data.name,
      permissions: childPermissions
    }, handleShowModal);
  };

  const loadPermissions = (data) => {
    // Identify which permissions have children based on key pattern
    const permissionsWithChildren = data.filter(perm => 
      data.some(other => other.key.startsWith(perm.key + '.'))
    );
    
    const parentKeys = new Set(permissionsWithChildren.map(p => p.key));
    
    // Identify child permissions (those that have a parent)
    const childKeys = new Set();
    data.forEach(perm => {
      const parts = perm.key.split('.');
      if (parts.length > 1) {
        // This is potentially a child, mark it
        childKeys.add(perm.key);
      }
    });
    
    // Create groups from parent permissions
    const grouped = [];
    
    // Add parent-child groups
    permissionsWithChildren.forEach(parent => {
      const children = data.filter(perm => {
        // Check if this permission is a direct child of the parent
        const parts = perm.key.split('.');
        const parentParts = parent.key.split('.');
        
        // It's a direct child if it has one more segment and starts with parent key
        return perm.key.startsWith(parent.key + '.') && 
               parts.length === parentParts.length + 1;
      });
      
      if (children.length > 0) {
        grouped.push({
          parent: { label: parent.name, value: parent.id, key: parent.key },
          children: children.map(child => ({ 
            label: child.name, 
            value: child.id,
            key: child.key 
          }))
        });
      }
    });
    
    // Add standalone permissions (not parent, not child)
    const standalonePermissions = data.filter(perm => 
      !parentKeys.has(perm.key) && !childKeys.has(perm.key)
    );
    
    standalonePermissions.forEach(standalone => {
      grouped.push({
        parent: { label: standalone.name, value: standalone.id, key: standalone.key },
        children: [] // No children, this is standalone
      });
    });

    setGroupedPermissions(grouped);
    
    // Keep flat list for reference
    setListPermissions(
      data.map((v) => {
        const isParent = parentKeys.has(v.key);
        return { 
          label: v.name, 
          value: v.id, 
          key: v.key,
          isParent 
        };
      })
    );
  };

  useEffect(() => {
    actListPermissions(loadPermissions);
  }, []);

  useEffect(() => {
    if (roleData) {
      reset({
        name: roleData.name,
        permissions: roleData.permissions?.map(p => p.id) || [],
      });
    }
  }, [roleData, reset]);

  return {
    control,
    handleSubmit,
    onSubmit,
    showModal,
    handleCloseModal,
    roleData,
    listPermissions,
    groupedPermissions,
  };
};

export default useRolesEdit;
